import { ACTION_LABELS, DEFAULT_BINDINGS, QUALITY_PRESETS, keyLabel, settings, type Action, type Quality } from '../../app/settings';
import { audio } from '../../game/audio/audio';
import { append, button, clear, h, modal } from '../dom';

/** Ajustes de gráficos, controles, sonido y accesibilidad (se guardan en este navegador). */
export function openSettings(info?: { renderer: string; maxTexture: number }) {
  const body = h('div', { class: 'settings' });
  const render = () => {
    clear(body);
    const s = settings.get();
    const row = (label: string, control: HTMLElement) => h('label', { class: 'set-row' }, h('span', null, label), control);
    const range = (key: 'sensitivity' | 'uiScale' | 'master' | 'music' | 'sfx' | 'renderScale' | 'viewDistance', min: number, max: number, step: number) =>
      h('input', {
        type: 'range',
        min,
        max,
        step,
        value: s[key],
        onInput: (e: Event) => settings.set({ [key]: Number((e.target as HTMLInputElement).value) }),
      });
    const toggle = (key: 'shadows' | 'invertY' | 'reduceEffects' | 'showFps' | 'showOthersFaces') =>
      h('input', { type: 'checkbox', checked: s[key], onChange: (e: Event) => settings.set({ [key]: (e.target as HTMLInputElement).checked }) });
    const quality = h(
      'select',
      {
        onChange: (e: Event) => {
          const q = (e.target as HTMLSelectElement).value as Quality;
          settings.set({ quality: q, ...QUALITY_PRESETS[q] });
          render();
        },
      },
      ...(['low', 'medium', 'high'] as Quality[]).map((q) => h('option', { value: q, selected: s.quality === q }, { low: 'Baja (equipos modestos)', medium: 'Media', high: 'Alta' }[q])),
    );

    let waiting: Action | null = null;
    const binds = h(
      'div',
      { class: 'binds' },
      ...(Object.keys(ACTION_LABELS) as Action[]).map((a) =>
        h(
          'div',
          { class: 'bind' },
          h('span', null, ACTION_LABELS[a]),
          button(keyLabel(s.bindings[a]), () => {
            waiting = a;
            const btn = binds.querySelector(`[data-a="${a}"]`) as HTMLElement;
            btn.textContent = 'Pulsa una tecla…';
            const onKey = (e: KeyboardEvent) => {
              e.preventDefault();
              e.stopPropagation();
              window.removeEventListener('keydown', onKey, true);
              if (e.code !== 'Escape' && waiting) settings.set({ bindings: { ...settings.get().bindings, [waiting]: e.code } });
              waiting = null;
              render();
            };
            window.addEventListener('keydown', onKey, true);
          }, 'btn small key', { 'data-a': a }),
        ),
      ),
    );

    append(body, [
      h('h3', null, '🎨 Gráficos'),
      row('Calidad', quality),
      row('Sombras', toggle('shadows')),
      row('Resolución', range('renderScale', 0.5, 1, 0.05)),
      row('Distancia de visión', range('viewDistance', 60, 220, 10)),
      row('Mostrar FPS', toggle('showFps')),
      info ? h('p', { class: 'muted small' }, `WebGL: ${info.renderer} · textura máx. ${info.maxTexture}px`) : null,
      h('h3', null, '🔊 Sonido'),
      row('Volumen general', range('master', 0, 1, 0.05)),
      row('Música', range('music', 0, 1, 0.05)),
      row('Efectos', range('sfx', 0, 1, 0.05)),
      h('h3', null, '🎮 Controles'),
      row('Sensibilidad del ratón', range('sensitivity', 0.2, 3, 0.05)),
      row('Invertir eje Y', toggle('invertY')),
      binds,
      button('Restaurar teclas', () => (settings.set({ bindings: { ...DEFAULT_BINDINGS } }), render()), 'btn ghost small'),
      h('h3', null, '♿ Accesibilidad'),
      row('Reducir efectos intensos (parpadeos, temblores, partículas)', toggle('reduceEffects')),
      row('Tamaño de la interfaz', range('uiScale', 0.8, 1.4, 0.05)),
      row('Mostrar las caras fotográficas de otros jugadores', toggle('showOthersFaces')),
      h('p', { class: 'muted small' }, 'Todas las señales sonoras importantes tienen también un aviso visual en pantalla.'),
      ]);
  };
  render();
  modal('Ajustes', body, [{ label: 'Listo', cls: 'btn primary', run: () => audio.play('click') }], { wide: true });
}
